<template>
  <VCard v-on-clickaway="turnOffEditing" class="pa-2 elevation-0 transparent">
    <LayoutImageUploadArea v-if="!imageSrc" @uploaded="setImage" />
    <div
      v-else
      :class="['image-elem-wrapper', { editing: showAdjuster, hovering: isHovering }]"
      v-on="isSummaryView ? {} : { mouseenter: onMouseEnter, mousemove: onMouseMove, mouseleave: onMouseLeave }"
    >
      <div class="image-elem-content">
        <img
          class="drag-handle image-elem-img"
          :src="imageSrc"
          :style="imgStyle"
          @click="isSummaryView ? null : toggleEditing()"
        />
        <LayoutImageResizeHandle
          v-if="loggedIn && !isSummaryView"
          :size="size"
          :ratio="ratio"
          :handle-on-top="handleOnTop"
          :is-hovering="isHovering"
          :is-resizing="isResizing"
          @resize="changeSize"
          @resize-start="isResizing = true"
          @resize-end="isResizing = false"
        />
      </div>
      <LayoutElementSettingsMenu
        v-if="!isSummaryView && loggedIn && isShowingLayoutEditUI"
        v-model="settingsOpen"
        :confirming-delete="confirmingDelete"
        @delete="onDelete"
        @done="turnOffEditing"
      >
        <div class="d-flex align-center mb-2">
          <span class="body grey--text text--darken-1 mr-3">Align</span>
          <VBtnToggle :value="align" mandatory class="align-toggle" @change="setAlign">
            <VBtn flat value="left"><VIcon>format_align_left</VIcon></VBtn>
            <VBtn flat value="center"><VIcon>format_align_center</VIcon></VBtn>
            <VBtn flat value="right"><VIcon>format_align_right</VIcon></VBtn>
          </VBtnToggle>
        </div>
        <div class="d-flex align-center mt-2">
          <VSlider :value="size" :min="20" :max="400" step="5" label="Height" color="grey darken-2" class="mr-3 mt-0" hide-details @input="size = $event" @change="changeSize" />
          <input :value="size" type="number" min="20" max="400" step="5" class="image-size-input" @change="onSizeInput" />
        </div>
      </LayoutElementSettingsMenu>
    </div>
  </VCard>
</template>

<script>
import { mapGetters } from 'vuex'
import { mixin as clickaway } from 'vue-clickaway'
import _get from 'lodash/get'
import DebouncedSave from '@mixins/debounced-save'
import LayoutElementEditing from '@mixins/layout-element-editing'
import LayoutElementSettingsMenu from './LayoutElementSettingsMenu'
import LayoutImageUploadArea from './LayoutImageUploadArea'
import LayoutImageResizeHandle from './LayoutImageResizeHandle'

export default {
  name: 'LayoutElementTypeImage',
  components: { LayoutElementSettingsMenu, LayoutImageUploadArea, LayoutImageResizeHandle },
  mixins: [clickaway, DebouncedSave, LayoutElementEditing],
  props: {
    element: {
      type: Object,
      required: true,
      default: () => {},
    },
    ratio: {
      type: Number,
      default: 1,
    },
    isSummaryView: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      size: _get(this.element, 'content.height', 100),
      src: null,
      isResizing: false,
      handleOnTop: false,
    }
  },
  computed: {
    ...mapGetters('auth', ['loggedIn']),
    imageSrc: {
      get() {
        return this.src || this.element.content?.src || ''
      },
      set(newSrc) {
        let element = { ...this.element }
        element.content.src = newSrc
        element.title = 'image'
        this.updateElement(element)
      },
    },
    height() {
      return this.size * this.ratio
    },
    align() {
      return this.element.content?.align || 'center'
    },
    imgStyle() {
      const margins = {
        left:   { marginLeft: '0',    marginRight: 'auto' },
        center: { marginLeft: 'auto', marginRight: 'auto' },
        right:  { marginLeft: 'auto', marginRight: '0'    },
      }
      return { maxHeight: `${this.height}px`, ...margins[this.align] }
    },
    showAdjuster() {
      return this.isEditing && this.isShowingLayoutEditUI
    },
  },
  watch: {
    // A height saved elsewhere only updates the display. Saving it from here
    // would make every logged-out TV try to write it back.
    'element.content.height'(val) {
      if (val && val !== this.size) {
        this.size = val
      }
    },
  },
  methods: {
    setImage(url) {
      this.imageSrc = url
      this.src = url
    },
    toggleEditing() {
      if (!this.loggedIn || !this.isShowingLayoutEditUI) return
      this.isEditing = !this.isEditing
    },
    // Only an owner's input saves the height: the resize handle, the
    // slider's change (its input event also fires on prop changes) or the
    // number field.
    changeSize(val) {
      this.size = val
      this.scheduleSave('height', this.updateHeight)
    },
    updateHeight() {
      this.updateElement({
        ...this.element,
        content: { ...this.element.content, height: this.size },
      })
    },
    setAlign(val) {
      this.updateElement({
        ...this.element,
        content: { ...this.element.content, align: val },
      })
    },
    onMouseEnter() {
      this.isHovering = true
      this.resetHoverTimer()
      const rect = this.$el.getBoundingClientRect()
      this.handleOnTop = rect.bottom > window.innerHeight * 0.75
    },
    resetHoverTimer() {
      clearTimeout(this.hoverTimer)
      this.hoverTimer = setTimeout(() => {
        if (!this.settingsOpen && !this.isResizing) {
          this.isHovering = false
        }
      }, 5000)
    },
    onSizeInput(e) {
      const val = parseInt(e.target.value, 10)
      if (!isNaN(val)) {
        this.changeSize(Math.max(20, Math.min(400, val)))
      } else {
        e.target.value = this.size
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.align-toggle.v-btn-toggle {
  box-shadow: none;
  ::v-deep .v-btn {
    min-width: 36px;
    height: 32px;
    padding: 0;
    .v-icon { font-size: 18px; }
  }
}
.image-elem-wrapper {
  position: relative;
}
.image-elem-content {
  position: relative;
}
.image-elem-img {
  display: block;
  max-width: 100%;
  height: auto;
  transition: max-height 0.15s ease;
}
.image-elem-wrapper.hovering,
.image-elem-wrapper.editing {
  ::v-deep .elem-settings-btn {
    opacity: 1;
  }
}
.image-size-input {
  width: 56px;
  padding: 4px 6px;
  font-size: 13px;
  text-align: center;
  border: 1px solid #ccc;
  border-radius: 4px;
  outline: none;

  &:focus {
    border-color: #1976d2;
  }

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    appearance: none;
    margin: 0;
  }
  -moz-appearance: textfield;
  appearance: textfield;
}
</style>
